<script lang="ts" setup>
	import type { WPCategoryResponse } from '~/server/type'
	import type { uiTagConfig } from '../ui/pill.vue'

	type articleCardUI = {
		base: string
		width: string
		height?: string
		overflow: string
		rounded: string
		border: string
		bg: string
		imgWrapper: Partial<{
			base: string
			height: string
			width: string
			bg: string
		}>
		content: Partial<{
			padding: string
			tag: Partial<uiTagConfig>
			article: Partial<{
				base: string
				margin: string
				spacer: string
				title: Partial<{
					base: string
					size: string
					color: string
					font: string
				}>
				excerpt: Partial<{
					base: string
					font: string
					size: string
					color: string
				}>
			}>
		}>
	}

	defineProps<{
		title: string
		to?: string
		exercept: string
		category?: WPCategoryResponse
		imgSrc?: string
		loading?: boolean
		ui?: Partial<articleCardUI>
	}>()
	const defaultCls: articleCardUI = {
		base: '',
		width: 'max-w-[400px] flex-1 w-full',
		// height: 'min-h-[380px]',
		overflow: 'overflow-hidden',
		rounded: 'rounded-lg',
		border: ' border border-gray-200 ',
		bg: 'bg-white',
		imgWrapper: {
			base: 'header',
			height: 'min-h-[210px]',
			width: 'w-full',
			bg: ' bg-gray-100'
		},
		content: {
			padding: 'sm:p-5 p-3  inline-flex items-center flex-wrap gap-2',
			tag: {
				base: 'capitalize group-hover:bg-base-50 group-hover:text-main text-gray-700 transition-colors',
				font: 'font-medium',
				padding: 'p-[6px_12px]',
				size: ' text-xs leading-[18.373px]',
				default: 'bg-gray-100'
			},
			article: {
				base: '',
				margin: 'text mt-5',
				spacer: 'sm:space-y-3 space-y-1.5',
				title: {
					base: '',
					size: 'sm:text-xl text-lg sm:leading-[22.4px] leading-tight',
					color: 'text-blue-950',
					font: 'font-semibold'
				},
				excerpt: {
					base: '',
					font: 'font-normal',
					size: 'sm:text-base text-sm sm:leading-[22.4px]',
					color: 'text-gray-600 '
				}
			}
		}
	}
</script>
<template>
	<client-only>
		<u-skeleton
			v-if="loading"
			class="min-h-[380px] max-w-[400px] flex-1 w-full"
			:ui="{
				rounded: ui?.rounded ?? defaultCls.rounded,
				background: 'bg-gray-300',
				strategy: 'override',
				base: 'animate-ping'
			}"
		/>

		<div
			v-else
			:title="title"
			:class="[
				ui?.base ?? defaultCls.base,
				ui?.bg ?? defaultCls.bg,
				ui?.border ?? defaultCls.border,
				ui?.height ?? defaultCls.height,
				ui?.overflow ?? defaultCls.overflow,
				ui?.rounded ?? defaultCls.rounded,
				ui?.width ?? defaultCls?.width,
				'group hover:border-main ease-linear transition-all'
			]"
		>
			<nuxt-link :to :custom="!to" prefetch>
				<div
					v-if="imgSrc"
					:class="[
						ui?.imgWrapper?.base ?? defaultCls.imgWrapper.base,
						ui?.imgWrapper?.bg ?? defaultCls.imgWrapper.bg,
						ui?.imgWrapper?.height ?? defaultCls.imgWrapper.height,
						ui?.imgWrapper?.width ?? defaultCls.imgWrapper.width
					]"
				>
					<img
						:src="imgSrc"
						:alt="title"
						:width="384"
						:height="210"
						loading="lazy"
						class="object-cover max-h-[210px] min-h-[inherit] overflow-hidden w-full text-xs text-gray-600"
					/>
				</div>

				<div :class="[ui?.content?.padding ?? defaultCls.content?.padding]">
					<template v-if="Array.isArray(category)">
						<ui-pill
							v-for="(item, index) in category"
							:key="`category-${item}-{index}`"
							:label="item.name"
							:ui="{
								display: category ? 'inline' : 'hidden',
								...(ui?.content?.tag || defaultCls.content.tag)
							}"
						/>
					</template>

					<article
						:class="[
							ui?.content?.article?.base ?? defaultCls.content?.article?.base,
							ui?.content?.article?.margin ?? defaultCls.content.article?.margin,
							ui?.content?.article?.spacer ?? defaultCls.content.article?.spacer
						]"
					>
						<h5
							:class="[
								ui?.content?.article?.title?.base ?? defaultCls.content.article?.title?.base,
								ui?.content?.article?.title?.color ?? defaultCls.content.article?.title?.color,
								ui?.content?.article?.title?.font ?? defaultCls.content.article?.title?.font,
								ui?.content?.article?.title?.size ?? defaultCls.content.article?.title?.size
							]"
							v-html="title"
						/>
						<p
							:class="[
								ui?.content?.article?.excerpt?.base ?? defaultCls.content.article?.excerpt?.base,
								ui?.content?.article?.excerpt?.color ?? defaultCls.content.article?.excerpt?.color,
								ui?.content?.article?.excerpt?.font ?? defaultCls.content.article?.excerpt?.font,
								ui?.content?.article?.excerpt?.size ?? defaultCls.content.article?.excerpt?.size
							]"
							v-html="exercept"
						/>
					</article>
				</div>
			</nuxt-link>
		</div>
	</client-only>
</template>

<style scoped>
	.text * {
		font-feature-settings:
			'liga' off,
			'clig' off;
	}
	h5 > p {
		margin: 0;
		padding: 0;
	}
</style>
